import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

/**
 * Prevent Radix UI Select / Popover from ever emitting the harmless
 * "ResizeObserver loop completed with undelivered notifications" warning
 * that CRA's dev overlay treats as an uncaught error.
 *
 * We wrap the native ResizeObserver so callbacks fire on the next animation
 * frame instead of synchronously — this eliminates the loop entirely.
 */
if (typeof window !== "undefined" && window.ResizeObserver) {
  const NativeRO = window.ResizeObserver;
  class SafeResizeObserver extends NativeRO {
    constructor(cb) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return;
          cb(entries, observer);
        });
      });
    }
  }
  window.ResizeObserver = SafeResizeObserver;
}

// Belt-and-braces: if a residual RO warning does slip through, don't let it
// reach CRA's error overlay.
const RO_MSGS = [
  "ResizeObserver loop completed with undelivered notifications",
  "ResizeObserver loop limit exceeded",
];
const isRO = (msg) => RO_MSGS.some((m) => (msg || "").includes(m));
window.addEventListener(
  "error",
  (e) => {
    if (isRO(e?.message)) {
      e.stopImmediatePropagation();
      e.preventDefault();
    }
  },
  true, // capture phase, before CRA's listener
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
